import React, { useEffect, useState } from "react";
import AgGridWrapper from "../UI/AgGrid";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { FormikInput, PrimaryButton, SecondaryButton } from "../UI";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import ConfirmModal from "../UI/Modal/confirmModal";
import Loader from "../UI/Loader/Loader";

const pathSchema = Yup.object().shape({
    path: Yup.string().required("path is required"),
});

const UpdateGithubEntryAssociationModal = ({
    isOpen,
    onClose,
    associatedExperiences = [], // fetched from API
    onSaveAsNewOffer,
    onUpdateAssociatedExperience,
    onDelinkExperience,
    loading
}) => {
    const { content } = useContentfulContext();
    const text = content?.GITHUB_ENTRY_DETAILS_CONTENT?.update_modal || {};
    const [activations, setActivations] = useState([]);
    const [showNewOfferModal, setShowNewOfferModal] = useState(false);
    const [showDelinkConfirmModal, setShowDelinkConfirmModal] = useState(false);

    const columnDefs = [
        { headerName: text.column_headers?.experience_name || "Experience Name", field: "name", flex: 1 },
        { headerName: text.column_headers?.experience_id || "Experience Id", field: "id", flex: 1 },
    ];

    useEffect(() => {
        if (!associatedExperiences || associatedExperiences.length === 0) {
            setActivations([]);
            return;
        }

        const formatted = associatedExperiences.map((exp) => ({
            id: exp.experience_id,
            name: exp.experience_name,
        }));

        setActivations(formatted);
    }, [associatedExperiences]);


    return (
        <PrimaryModal isOpen={isOpen} hideModal={onClose} title={text.title || "Update Github"} widthClass="min-w-max">
            {loading && <Loader/>}
            <div className="p-6 space-y-6">
                <p className="text-sm font-medium text-gray-700">
                    {text.configure_text || "This Github entry is associated with the following experiences:"}
                </p>

                {activations.length > 0 ? (
                    <div style={{ height: "280px", overflow: "auto", width: "100%" }}>
                        <AgGridWrapper
                            rowData={activations}
                            columnDefs={columnDefs}
                            domLayout="autoHeight"
                        />
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm">
                        {text.no_associations_text || "No associated experiences found."}
                    </p>
                )}

                <div className="flex justify-end gap-3 pt-4">
                    <SecondaryButton handleClick={onClose}>   {text.buttons?.cancel || "Cancel"}</SecondaryButton>

                    <PrimaryButton handleClick={() => setShowNewOfferModal(true)}>
                        {text.buttons?.save_as_new_entry || "Save as New Entry"}
                    </PrimaryButton>

                    <PrimaryButton
                        handleClick={() => {
                            if (onUpdateAssociatedExperience)
                                onUpdateAssociatedExperience();
                            onClose();
                        }}
                    >
                        {text.buttons?.update_associated_experience || "Update Associated Experience"}
                    </PrimaryButton>

                    <PrimaryButton
                        handleClick={() => setShowDelinkConfirmModal(true)}
                    >
                        {text.buttons?.delink_experience || "Delink Experience"}
                    </PrimaryButton>
                </div>
            </div>
            {showNewOfferModal && (
                <PrimaryModal
                    isOpen={showNewOfferModal}
                    hideModal={() => setShowNewOfferModal(false)}
                    title={text.modal_new_entry_title || "Create Github Entry"}
                    width="500px"
                >
                    <div className="p-6 space-y-6">
                        <Formik
                            initialValues={{ path: "" }}
                            validationSchema={pathSchema}
                            onSubmit={(values) => {
                                console.log("Submitted:", values.path);
                                onSaveAsNewOffer(values.path);
                                setShowNewOfferModal(false);
                                onClose();
                            }}
                        >
                            {() => (
                                <Form className="space-y-4 md:space-y-6">
                                    <FormikInput
                                        type="text"
                                        id="path"
                                        name="path"
                                        label={text.field_label || "Path"}
                                    />

                                    <div className="flex items-center justify-end space-x-2">
                                        <PrimaryButton type="submit">{text.modal_new_entry_submit || "Create"}</PrimaryButton>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </PrimaryModal>
            )}

            {showDelinkConfirmModal && (
                <ConfirmModal
                    isOpen={showDelinkConfirmModal}
                    hideModal={() => setShowDelinkConfirmModal(false)}
                    title="Confirm Delink"
                    description="Are you sure you want to delink this entry from all associated experiences? This also updates the entry."
                    ConfirmText="Yes, Delink"
                    CancelText="Cancel"
                    loading={false}
                    handleConfirmation={async () => {
                        setShowDelinkConfirmModal(false);
                        if (onDelinkExperience) await onDelinkExperience();
                        onClose();
                    }}
                />
            )}
        </PrimaryModal>
    );
};

export default UpdateGithubEntryAssociationModal;
